import type { DebounceSettings } from 'lodash-es';
import { debounce } from 'lodash-es';

export class PromiseResolver<T> {
  private resolve_: (arg: T) => void = () => {};
  private reject_: (arg: any) => void = () => {};
  private isFulfilled_: boolean = false;
  private readonly promise_: Promise<T>;

  constructor() {
    this.promise_ = new Promise((resolve, reject) => {
      this.resolve_ = (resolution: T) => {
        resolve(resolution);
        this.isFulfilled_ = true;
      };
      this.reject_ = (reason: any) => {
        reject(reason);
        this.isFulfilled_ = true;
      };
    });
  }

  get isFulfilled(): boolean {
    return this.isFulfilled_;
  }

  get promise(): Promise<T> {
    return this.promise_;
  }

  get resolve(): (arg: T) => void {
    return this.resolve_;
  }

  get reject(): (arg?: any) => void {
    return this.reject_;
  }
}

export const debouncePromise = (
  fn: (...args: any) => any,
  wait = 200,
  options?: DebounceSettings
) => {
  let currentPromise: Promise<any> | null = null;
  let resolveFn: any = null;
  let rejectFn: any = null;
  const debouncedFn = debounce(
    async function (...args: any[]) {
      try {
        const result = await fn.apply(this, args);
        if (resolveFn) resolveFn(result);
      } catch (error) {
        if (rejectFn) rejectFn(error);
      } finally {
        currentPromise = null;
        resolveFn = null;
        rejectFn = null;
      }
    },
    wait,
    options
  );

  return function (...args: any[]) {
    debouncedFn.cancel();
    if (!currentPromise) {
      currentPromise = new Promise((resolve, reject) => {
        resolveFn = resolve;
        rejectFn = reject;
      });
    }
    debouncedFn.apply(this, args);
    return currentPromise;
  };
};
